<script lang="ts">
  import { session, currentStep, records, selectedChecklistId, committeeComments } from '../stores';
  import { checklists } from '../data/checklists';
  import type { InspectionRecord } from '../types';
  import { renderBold, renderBoldInline } from '../helpers';

  const userSession = $derived($session);
  const checklistId = $derived($selectedChecklistId);
  const currentChecklist = $derived(
    checklists.find(c => c.id === checklistId)
  );

  const currentRecords = $derived($records);
  function formatNowForStore() {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${mm}/${dd} ${hh}:${min}`;
  }

  function toggleItem(itemId: string) {
    records.update(prev => {
      const newRecords = { ...prev };
      if (newRecords[itemId]) {
        // 取消勾選：移除該項目的紀錄
        delete newRecords[itemId];
      } else {
        // 勾選：儲存狀態與當下時間（格式 MM/DD HH:MM）
        newRecords[itemId] = {
          itemId,
          status: 'ok',
          timestamp: formatNowForStore()
        };
      }
      return newRecords;
    });
  }

  // 已移除提交/完成頁面邏輯，改由 header 列印流程或直接截圖分享

  // Committee comments are managed via the committeeComments store,
  // which is persisted by the active StorageAdapter (local or remote).
  import { onMount } from 'svelte';
  let committeeComment = $state('');
  let currentChecklistId: string | null = null;

  onMount(() => {
    // 訂閱選中的 checklist，並載入對應的管委留言
    const unsubChecklist = selectedChecklistId.subscribe(id => {
      currentChecklistId = id;
      if (id) {
        // 從 committeeComments store 讀取（已由 StorageAdapter 載入）
        committeeComment = $committeeComments[id] ?? '';
      } else {
        committeeComment = '';
      }
    });

    // 當 committeeComments store 更新時同步本地變數（例如從遠端載入後）
    const unsubComments = committeeComments.subscribe(comments => {
      if (currentChecklistId) {
        committeeComment = comments[currentChecklistId] ?? '';
      }
    });

    return () => { unsubChecklist(); unsubComments(); };
  });

  function saveCommitteeComment() {
    if (!currentChecklistId) return;
    // 更新 store（由 App.svelte 的訂閱負責持久化到 adapter）
    committeeComments.update(prev => ({
      ...prev,
      [currentChecklistId!]: committeeComment || ''
    }));
    alert('意見已儲存');
  }
</script>

  <div class="w-full">
  {#if currentChecklist && userSession}
    <div class="mb-6 sm:mb-8">
      <div class="flex flex-col items-start mb-4">
        <div class="w-full">
          <h2 class="leading-tight font-bold text-gray-800 mb-2">
            {currentChecklist.title}
          </h2>
          {#if userSession.role === 'committee'}
            <div class="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg w-full">
              <label for="committee-comment" class="block text-sm sm:text-base font-semibold text-yellow-800 mb-2">管委意見（可選）</label>
              <textarea id="committee-comment" bind:value={committeeComment} rows="3" class="w-full p-3 rounded-md border-2 border-yellow-200"></textarea>
              <div class="mt-2 text-right">
                <button onclick={saveCommitteeComment} class="px-4 py-2 bg-yellow-600 text-white rounded-lg font-bold">送出意見</button>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- 檢查項目列表 -->
    <div class="mb-4 sm:mb-8 sm:space-y-3">
      {#each currentChecklist.items as item, idx (item.id)}
        <div
          class="w-full text-left flex items-start transition-all"
          class:sm:bg-green-50={currentRecords[item.id]}
          class:sm:border-green-500={currentRecords[item.id]}
          class:sm:bg-white={!currentRecords[item.id]}
          class:sm:border-gray-200={!currentRecords[item.id]}
          style="
            /* Mobile: no card, tight row with left color bar */
            border-left: 4px solid {currentRecords[item.id] ? '#22c55e' : '#d1d5db'};
            padding: 6px 0 6px 8px;
            gap: 6px;
            align-items: flex-start;
          "
        >
          <!-- Check button: fixed 24px on mobile, 48px on desktop -->
          <button
            onclick={(e) => { e.stopPropagation(); toggleItem(item.id); }}
            aria-pressed={!!currentRecords[item.id]}
            class="shrink-0 flex items-center justify-center focus:outline-none sm:w-12 sm:h-12"
            style="width: 24px; height: 24px; margin-top: 2px;"
          >
            {#if currentRecords[item.id]}
              <div class="bg-green-500 rounded-full flex items-center justify-center sm:w-12 sm:h-12" style="width: 24px; height: 24px;">
                <span class="text-white leading-none" style="font-size: 14px;">✓</span>
              </div>
            {:else}
              <div class="border-2 border-gray-400 rounded-full sm:border-4 sm:w-12 sm:h-12" style="width: 24px; height: 24px;"></div>
            {/if}
          </button>

          <div class="grow selectable markdown-content" style="min-width: 0;">
            <div class="flex items-baseline" style="gap: 4px;">
              <span class="text-gray-400 font-bold shrink-0 sm:text-xl" style="font-size: 0.75em;">{idx + 1}.</span>
              <p
                class="font-bold selectable leading-snug sm:text-3xl"
                class:text-green-700={currentRecords[item.id]}
                class:text-gray-800={!currentRecords[item.id]}
                style="font-size: 1em; margin: 0;"
              >
                <!-- Use inline render to avoid <p> wrapping that pushes * to new line -->
                {@html renderBoldInline(item.text) + (item.required ? '<span class="text-red-500" style="font-size:0.7em;margin-left:2px;">*</span>' : '')}
              </p>
            </div>
            {#if item.hint}
              <div class="text-blue-700 font-semibold selectable sm:text-base sm:mt-2" style="font-size: 0.75em; margin: 1px 0 0 0;">
                {@html renderBold(item.hint)}
              </div>
            {/if}
            {#if currentRecords[item.id] && currentRecords[item.id].timestamp}
              <p class="text-gray-500 sm:text-base sm:mt-2" style="font-size: 0.7em; margin: 1px 0 0 0;">
                ✓ {currentRecords[item.id].timestamp}
              </p>
            {/if}
          </div>
        </div>
      {/each}
    </div>

    <!-- 提交按鈕已移除；使用 header 的印表或建議截圖功能保存結果 -->
  {/if}
</div>
